/**
 * Live Localhost Preview — the DURABLE half of the vocabulary (APP_PREVIEW_V1).
 *
 * The ephemeral half lives in events.ts (TaskPreviewReadyPayload: url + token,
 * good only while the frame is lit). This file holds what survives the turn:
 * the pointer stored in `tasks.live_preview`, and the wake route's reply.
 *
 * WHY NO URL/TOKEN IN THE POINTER: Blaxel scales a sandbox to zero ~15s after
 * inactivity — the dev server's process is frozen in the memory snapshot and
 * resumes on the next exec, but the preview URL/token minted at announce time
 * are short-lived. So the durable record is the FACT (this task has an app on
 * the reserved port), and url+token are re-minted per wake.
 *
 * WHY THE POINTER CARRIES A COMMAND: resuming is not always enough. A staging
 * drill found an agent-started dev server that survived freeze/resume as a
 * process — still holding its LISTEN socket, kernel still completing handshakes
 * — while userspace served nothing at all (curl timed out with zero bytes). The
 * same server launched as a NAMED Blaxel process resumed perfectly. So a wake
 * must be able to KILL and RELAUNCH, and to relaunch it needs the command and
 * the directory it ran in. Captured at announce, because that is the only
 * moment we are guaranteed a healthy process to read them from.
 */

/**
 * The reserved app ports — a POOL, framework defaults first so most apps land
 * hosted with no port flag (3000 Next/Remotion/Express, 5173 Vite, 8000
 * Django/FastAPI, 5000 Flask, 8501 Streamlit). Ports are permanent once used:
 * add, never renumber. Never BLAXEL_RESERVED_PORTS.
 */
export const APP_PORT_POOL: readonly number[] = [
  3000, 3001, 3002, 3003, 3004,
  5173, 5174, 5175, 5176, 5177,
  8000, 8001, 8002, 8003, 8004,
  5000, 5001, 5002, 5003, 5004,
  8501, 8502, 8503, 8504, 8505,
];
const POOL_SET: ReadonlySet<number> = new Set(APP_PORT_POOL);

/** The ports Blaxel keeps for the box itself (its sandbox API among them): never an app's. */
export const BLAXEL_RESERVED_PORTS: readonly number[] = [80, 443, 8080];
export function isPoolPort(port: number): boolean { return POOL_SET.has(port); }

/** Compat for one release: the port every pre-pool sandbox already exposes. */
export const APP_PREVIEW_PORT = 5173;

/** The Blaxel preview name for a pool port. 5173 keeps its pre-pool name so
 *  existing sandboxes keep their link; every other port is app-<port>. */
export function previewNameForPort(port: number): string {
  return port === APP_PREVIEW_PORT ? 'app-preview' : `app-${port}`;
}

export type TaskAppState = 'running' | 'stopped';
export type TaskAppStopReason = 'inactivity' | 'user' | 'replaced' | 'agent';

/** One task_apps row. snake_case: these are the stored keys, passed through verbatim. */
export interface TaskApp {
  task_id: string;
  port: number;
  /** The app's name as it reads on its window (declared by the agent). */
  name?: string;
  /** One line: what this app is (declared by the agent). */
  caption?: string;
  /** The app's face: its page, shot on the creations poster rail at announce
   *  and at every turn end while it runs. Its owner's door,
   *  `/api/apps/<port>/thumb?v=<stamp>` (a 60 s pass); absent until ready. */
  thumb_url?: string;
  path?: string;
  command?: string;
  cwd?: string;
  state: TaskAppState;
  announced_at: string;
  last_viewed_at: string | null;
  stopped_at: string | null;
  stop_reason: TaskAppStopReason | null;
}

/** apps:changed — the user's whole list, every time. Level-triggered: the web
 *  replaces its list, never patches it. */
export interface AppsChangedPayload { apps: TaskApp[] }

export type AnnounceError =
  | { ok: false; error: 'port_not_in_pool'; pool: number[] }
  | { ok: false; error: 'running_cap'; cap: number; running: Array<{ port: number; task_id: string; caption?: string; last_viewed_at: string | null }> }
  /** `not_on_this_computer`: the user's box runs on a host previews cannot reach yet (box hosts, Plan 2c). */
  | { ok: false; error: 'sandbox_unavailable' | 'preview_unavailable' | 'not_on_this_computer' };

export type AppActionResponse =
  /** `tokenParam`: the query parameter the token rides in, when it is not Blaxel's `bl_preview_token` (boat: `_token`).
   *  A Blaxel answer carries no such key. */
  | { ok: true; url: string; token: string; port: number; caption?: string; path?: string; tokenParam?: string }
  /** `sandbox_unavailable` is a Blaxel box we could not reach just now (try again); `not_on_this_computer`
   *  is a box on a host previews cannot reach yet (box hosts, Plan 2c): a state, so no retry. */
  | { ok: false; error: 'server_gone' | 'restart_failed' | 'preview_unavailable' | 'sandbox_unavailable' | 'not_found' | 'not_on_this_computer' }
  /**
   * The wake-door credit gate (founder 09-03, credits first-class): the balance
   * is exhausted, so the sandbox was NOT resumed — no wake, no start, no stop.
   * A business outcome like the others (rides a 200 with ok:false, so the
   * client's transport-failure fallback never swallows it); the surface walls
   * with an Upgrade action instead of a spinner.
   */
  | { ok: false; error: 'out_of_credits' }
  /**
   * Starting this app would put the user past APP_RUNNING_CAP. Its own member
   * because it carries the cap: the surface says which number was hit rather
   * than "too many", and the refusal is a REFUSAL, never a silent kill of
   * whichever app the user was not looking at. Mirrors AnnounceError's
   * running_cap, minus the running list the agent needs and a person does not.
   */
  | { ok: false; error: 'running_cap'; cap: number };

/**
 * `tasks.live_preview` — snake_case because these are the STORED jsonb keys,
 * passed through verbatim from Postgres to the client with no remapping layer.
 * At most one task per user is non-NULL (the announce path enforces it).
 */
export interface LivePreviewPointer {
  /** The sandbox's reserved app-preview port (validated before the write). */
  port: number;
  /** The agent's optional label for the app, shown on the dormant card. */
  caption?: string;
  /**
   * The app's entry page, when it is not at '/' — e.g. '/hello-vestra.html'.
   * Absent means root, which is what every pointer written before this field
   * existed means too. A founder drill found the cost of not having it: the
   * agent served a whole directory and the frame opened on a file listing
   * instead of the page it had just built.
   */
  path?: string;
  /** ISO timestamp of the announce that last lit this frame. */
  announced_at: string;
  /**
   * Argv of the process found listening on the port at announce time, read from
   * /proc/<pid>/cmdline. Absent when the capture failed or on pointers written
   * before this field existed — the wake then degrades to "probe only", which is
   * exactly the pre-restart-lane behavior.
   */
  command?: string;
  /** That process's working directory (/proc/<pid>/cwd), so a relaunch runs where it ran. */
  cwd?: string;
}

/**
 * POST /api/tasks/:taskId/preview/wake — reply.
 *
 * All failures are BUSINESS outcomes, not errors: they ride a 200 with ok:false
 * so the web renders a calm state instead of an error toast — the same
 * structured-field contract announce_preview uses.
 *
 *   server_gone     the app is not answering and we have no command to relaunch
 *                   it with (a pointer written before the restart lane, or a
 *                   capture that failed). Nothing left to try.
 *   restart_failed  we DID relaunch the captured command and the port still
 *                   never came up. The distinction matters: server_gone means
 *                   we never tried, restart_failed means we tried and the app
 *                   itself is broken.
 *   running_cap     the app is stopped and starting it would pass the cap. Only
 *                   the per-port wake can answer this, since only it can start
 *                   an app; the compat route above wakes a pointer and never
 *                   counts. Carries the cap so the surface can name the number.
 */
export type PreviewWakeResponse = AppActionResponse;

/** Cap on a stored entry path. Long enough for a real file path, short enough
 *  that nothing interesting fits in the jsonb or the frame's URL. */
export const PREVIEW_PATH_MAX = 200;

/**
 * Is this an entry path we are willing to paste onto the preview origin?
 *
 * Absolute, bounded, no traversal, and NOT protocol-relative — '//evil.example'
 * concatenated onto an empty origin would send the frame somewhere else
 * entirely, and the web builds the src by concatenation.
 */
export function isSafePreviewPath(p: string): boolean {
  return (
    p.length > 0 &&
    p.length <= PREVIEW_PATH_MAX &&
    p.startsWith('/') &&
    !p.startsWith('//') &&
    !p.includes('..')
  );
}

/**
 * Normalize an agent-supplied entry path, or give back undefined for anything
 * that should be treated as "no path" (which renders as root, the old behavior).
 *
 * Query and fragment are dropped: an entry path names a PAGE, and the frame's
 * own auth rides the query string — a fragment would swallow the token, and a
 * query would fight with it. '/' normalizes away too, since root is the absence
 * of a path rather than a value worth storing.
 */
export function sanitizePreviewPath(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const bare = raw.trim().replace(/[?#].*$/, '');
  if (bare === '/' || !isSafePreviewPath(bare)) return undefined;
  return bare;
}
